import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, ExternalLink, CheckCircle2, Home,
  Type, Image as ImageIcon, Link as LinkIcon, Plus, Minus, MoreHorizontal,
  Send, X, Camera, SquareDashedMousePointer, AlertTriangle,
  Clock, AlertCircle, Bug, ArrowRight, PencilLine,
} from 'lucide-react';
import FileUploadField from '../../components/dashboard/FileUploadField';
import SpotlightTour from '../../components/dashboard/SpotlightTour';
import InlineEditPanel from '../../components/portal/inline-edit/InlineEditPanel';
import useInlineEditBridge from '../../components/portal/inline-edit/useInlineEditBridge';
import { pluralize, PHONE_WIDTH, PHONE_CHECK_HEIGHT } from '../../components/portal/inline-edit/inlineEdit';
import { VISUAL_EDITOR_ENABLED } from '../../config/featureFlags';
import { usePortalScope } from '../../context/PortalScopeContext';
import { api, ApiError } from '../../lib/api';

const VE_TOUR_KEY = 'nexsav_visual_editor_tour_seen';

const CHANGE_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'image', label: 'Image', icon: ImageIcon },
  { value: 'link', label: 'Link', icon: LinkIcon },
  { value: 'new_section', label: 'New', icon: Plus },
  { value: 'remove', label: 'Remove', icon: Minus },
  { value: 'other', label: 'Other', icon: MoreHorizontal },
];

const BEFORE_AFTER_TYPES = new Set(['text', 'link']);
const PRIORITIES = [
  { value: 'low', label: 'Low', icon: Clock, color: 'text-green-400', ring: 'border-green-400/40 bg-green-400/[0.06]' },
  { value: 'medium', label: 'Medium', icon: AlertCircle, color: 'text-yellow-400', ring: 'border-yellow-400/40 bg-yellow-400/[0.06]' },
  { value: 'high', label: 'High', icon: AlertTriangle, color: 'text-orange-400', ring: 'border-orange-400/40 bg-orange-400/[0.06]' },
  { value: 'critical', label: 'Critical', icon: Bug, color: 'text-red-400', ring: 'border-red-400/40 bg-red-400/[0.06]' },
];

const inputClasses =
  'w-full px-3.5 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';
const labelClasses = 'block text-[10px] font-medium text-gray-500 mb-1.5 tracking-wider uppercase';

const BOX_STROKE = '#B0FC38';

export default function VisualEditor() {
  const { account } = usePortalScope();
  const navigate = useNavigate();
  const location = useLocation();
  const iframeRef = useRef(null);
  // Hidden phone-size frame that checks every inline change at 390px.
  const measureFrameRef = useRef(null);

  // A per-session token the bridge echoes back; presence-gates the bridge so it
  // stays dormant for normal visitors. (Production will sign this.)
  const editorToken = useMemo(() => Math.random().toString(36).slice(2) + Date.now().toString(36), []);
  const portalOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  const siteOrigin = useMemo(() => {
    try { return account?.siteUrl ? new URL(account.siteUrl).origin : null; } catch { return null; }
  }, [account]);

  const initialSrc = useMemo(() => {
    if (!siteOrigin) return null;
    const u = new URL('/', siteOrigin);
    u.searchParams.set('nexsav_editor', editorToken);
    u.searchParams.set('nexsav_portal', portalOrigin);
    return u.toString();
  }, [siteOrigin, editorToken, portalOrigin]);

  // What the bridge tells us the user is currently looking at.
  const [view, setView] = useState({ path: '/', title: '', scrollY: 0, viewportWidth: 0, viewportHeight: 0 });
  const [bridgeSeen, setBridgeSeen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [tourDemoPending, setTourDemoPending] = useState(false);

  // Run the guided tour when arriving from the Content Edits tour, or on the
  // first ever visit. Persist "seen" so it doesn't nag.
  useEffect(() => {
    if (!initialSrc) return undefined;
    let seen = false;
    try { seen = localStorage.getItem(VE_TOUR_KEY) === '1'; } catch { /* ignore */ }
    if (location.state?.tour || !seen) {
      const t = setTimeout(() => setShowTour(true), 450); // let the frame paint
      return () => clearTimeout(t);
    }
    return undefined;
  }, [initialSrc, location.state]);

  const closeTour = () => {
    setShowTour(false);
    setTourDemoPending(false);
    resetCapture(); // clear the demo capture/box so they start fresh
    try { localStorage.setItem(VE_TOUR_KEY, '1'); } catch { /* ignore */ }
  };

  // Capture + annotation state.
  const [capturing, setCapturing] = useState(false);
  const [capture, setCapture] = useState(null); // { dataUrl, pagePath, naturalW, naturalH }
  const [box, setBox] = useState(null); // natural-image coords { x, y, w, h }
  const [drawing, setDrawing] = useState(false);
  const drawStart = useRef(null);
  const imgRef = useRef(null);

  // Form state.
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [changeType, setChangeType] = useState('text');
  const [currentContent, setCurrentContent] = useState('');
  const [desiredContent, setDesiredContent] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState('');

  // "Edit text & images" (inline edits, docs/inline-edit-lab.md §8). Only when
  // the account's effective switch is on (accounts.inline_edit_mode 'on', or
  // 'auto' for a Maintained school on its live domain — the server decides);
  // off = the capture flow above, untouched.
  const inlineAvailable = VISUAL_EDITOR_ENABLED && account?.inlineEditEnabled === true;

  // Preview mode ("Sign in as principal") renders a sticky banner above the
  // portal layout. The editor is a fixed overlay inside that layout's stacking
  // context, so the banner paints over the editor's own header (Back, the edit
  // toggle, Home/Reload). Leave the banner's height free instead.
  const [topOffset, setTopOffset] = useState(0);
  useEffect(() => {
    const banner = () => document.querySelector('[data-testid="impersonation-banner"]');
    const measure = () => setTopOffset(banner()?.offsetHeight || 0);
    measure();
    window.addEventListener('resize', measure);
    const el = banner();
    const ro = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);
  const [editMode, setEditMode] = useState(false);
  const [inlineBusy, setInlineBusy] = useState(false);
  // A site page with the editor parameters (both frames use this).
  const frameUrl = useCallback((path, params = {}) => {
    if (!siteOrigin) return null;
    const u = new URL(path || '/', siteOrigin);
    u.searchParams.set('nexsav_editor', editorToken);
    u.searchParams.set('nexsav_portal', portalOrigin);
    Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.toString();
  }, [siteOrigin, editorToken, portalOrigin]);
  const inline = useInlineEditBridge({
    iframeRef, measureFrameRef, frameUrl, siteOrigin, token: editorToken, enabled: inlineAvailable && editMode,
  });
  const phoneFrame = editMode && inline.widthMode === 'phone';

  // Reload persistence: a publish (or undo) still running when the page was
  // reloaded opens the panel straight into its progress.
  const [inlineResume, setInlineResume] = useState(null);
  useEffect(() => {
    if (!inlineAvailable) return undefined;
    let alive = true;
    Promise.resolve(api.get('/api/edit-requests/inline-status'))
      .then((d) => {
        if (!alive || !Array.isArray(d?.inFlight) || !d.inFlight.length) return;
        setInlineResume(d);
        setEditMode(true);
      })
      .catch(() => { /* nothing to pick up */ });
    return () => { alive = false; };
  }, [inlineAvailable]);

  // Listen for the bridge's navigation/scroll reports (cross-origin postMessage).
  useEffect(() => {
    if (!siteOrigin) return undefined;
    const onMessage = (e) => {
      if (e.origin !== siteOrigin) return;
      // The hidden phone-size frame reports its own page; it isn't the view.
      const hidden = measureFrameRef.current?.contentWindow;
      if (hidden && e.source === hidden) return;
      const d = e.data;
      if (!d || typeof d !== 'object' || typeof d.type !== 'string') return;
      if (d.type === 'NEXSAV_EDITOR_NAV' || d.type === 'NEXSAV_EDITOR_SCROLL') {
        setBridgeSeen(true);
        setView((prev) => ({
          path: d.path || prev.path,
          title: d.title || prev.title,
          scrollY: typeof d.scrollY === 'number' ? d.scrollY : prev.scrollY,
          viewportWidth: d.viewportWidth || prev.viewportWidth,
          viewportHeight: d.viewportHeight || prev.viewportHeight,
        }));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [siteOrigin]);

  const pingBridge = () => {
    try { iframeRef.current?.contentWindow?.postMessage({ type: 'NEXSAV_EDITOR_PING' }, siteOrigin); } catch { /* noop */ }
  };

  const goHome = () => { if (iframeRef.current && initialSrc) iframeRef.current.src = initialSrc; };

  // Load a page of the site in the frame (editor params kept), e.g. to check a
  // change on another page or to show a freshly published page (`bust`: a
  // cache-busting parameter so the browser fetches the new version).
  const openFramePath = useCallback((path, { bust = false } = {}) => {
    if (!iframeRef.current) return;
    const src = frameUrl(path, bust ? { nexsav_v: Date.now().toString(36) } : {});
    if (src) iframeRef.current.src = src;
  }, [frameUrl]);

  const toggleEditMode = () => {
    if (!editMode) {
      setSubmitted(null);
      setEditMode(true);
      return;
    }
    const n = inline.changeList.length;
    if (n > 0 && !window.confirm(`Discard ${pluralize(n, 'unpublished change')}?`)) return;
    inline.clearAll({ revertPage: true });
    inline.setWidthMode('desktop');
    setInlineResume(null);
    setEditMode(false);
  };
  const reloadFrame = () => { try { iframeRef.current?.contentWindow?.location.reload(); } catch { if (initialSrc) iframeRef.current.src = initialSrc; } };

  const resetCapture = useCallback(() => {
    setCapture(null);
    setBox(null);
    setTitle('');
    setPriority('medium');
    setCurrentContent('');
    setDesiredContent('');
    setDescription('');
    setAttachments([]);
    setError('');
  }, []);

  // Take a server-side screenshot of exactly what the user is viewing.
  const captureView = async () => {
    if (capturing) return;
    pingBridge(); // make sure the path/scroll are fresh
    setCapturing(true);
    setError('');
    const el = iframeRef.current;
    const width = view.viewportWidth || el?.clientWidth || 1280;
    const height = view.viewportHeight || el?.clientHeight || 800;
    const payload = { pagePath: view.path || '/', width, height, scrollY: view.scrollY || 0 };
    // The screenshot backend can be transiently at its browser limit (503,
    // retryable). Auto-retry with backoff so a momentary rate-limit blip
    // self-heals instead of dead-ending on an error the user has to re-click.
    const backoffs = [4000, 7000];
    try {
      for (let attempt = 0; ; attempt += 1) {
        try {
          const data = await api.post('/api/site-capture', payload);
          setCapture({ dataUrl: data.screenshot, pagePath: data.pagePath || view.path, naturalW: 0, naturalH: 0 });
          setBox(null);
          setSubmitted(null);
          setTitle((t) => t || view.title || ''); // prefill with the page title
          setError('');
          break;
        } catch (err) {
          const retryable = err instanceof ApiError && (err.status === 503 || err.data?.retryable === true);
          if (retryable && attempt < backoffs.length) {
            setError(`Screenshot service is busy, retrying (${attempt + 1}/${backoffs.length})…`);
            await new Promise((r) => setTimeout(r, backoffs[attempt]));
            continue;
          }
          setError(err instanceof ApiError ? err.message : 'Could not capture the page');
          break;
        }
      }
    } finally {
      setCapturing(false);
    }
  };

  // Tour demo: capture the page for real, then pre-fill the form so the
  // "Describe" / "Submit" steps look like a finished request. The box is drawn
  // by the effect below once the screenshot's natural size is known.
  const runTourCapture = useCallback(async () => {
    setTourDemoPending(true);
    await captureView();
    setTitle((t) => t || 'Homepage headline tweak');
    setChangeType('text');
    setCurrentContent('A school where curiosity leads the way.');
    setDesiredContent('Welcome to Maplewood Academy');
    setDescription('Please update the hero headline to the new wording (marked).');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draw a demo box once the captured image's natural dimensions are known.
  useEffect(() => {
    if (tourDemoPending && capture?.naturalW) {
      const NW = capture.naturalW;
      const NH = capture.naturalH;
      setBox({ x: NW * 0.06, y: NH * 0.1, w: NW * 0.55, h: NH * 0.16 });
      setTourDemoPending(false);
    }
  }, [tourDemoPending, capture]);

  // --- box drawing on the screenshot (coords kept in natural-image space) ---
  const toNatural = (clientX, clientY) => {
    const im = imgRef.current;
    const rect = im.getBoundingClientRect();
    const sx = (capture.naturalW || im.naturalWidth) / rect.width;
    const sy = (capture.naturalH || im.naturalHeight) / rect.height;
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left)) * sx;
    const y = Math.max(0, Math.min(rect.height, clientY - rect.top)) * sy;
    return { x, y };
  };
  const onDrawStart = (e) => {
    if (!capture) return;
    e.preventDefault();
    const p = toNatural(e.clientX, e.clientY);
    drawStart.current = p;
    setDrawing(true);
    setBox({ x: p.x, y: p.y, w: 0, h: 0 });
  };
  const onDrawMove = (e) => {
    if (!drawing || !drawStart.current) return;
    const p = toNatural(e.clientX, e.clientY);
    const s = drawStart.current;
    setBox({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const onDrawEnd = () => { setDrawing(false); drawStart.current = null; };

  const onImgLoad = () => {
    const im = imgRef.current;
    if (im) setCapture((c) => (c ? { ...c, naturalW: im.naturalWidth, naturalH: im.naturalHeight } : c));
  };

  // Burn the box into the screenshot and submit.
  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !capture) return;
    setSubmitting(true);
    setError('');
    try {
      const composited = await compositeBox(capture.dataUrl, box);
      const showBA = BEFORE_AFTER_TYPES.has(changeType);
      const data = await api.post('/api/edit-requests', {
        title: title.trim(),
        pagePath: capture.pagePath || view.path || '/',
        changeType,
        priority,
        description: description.trim(),
        currentContent: showBA ? currentContent.trim() : '',
        desiredContent: showBA ? desiredContent.trim() : '',
        sectionPreview: view.title || null,
        sectionScreenshot: composited,
        attachments,
      });
      setSubmitted(data.editRequest);
      resetCapture();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (!account) return null;

  const hasBox = box && box.w > 4 && box.h > 4;
  const showBeforeAfter = BEFORE_AFTER_TYPES.has(changeType);
  const NW = capture?.naturalW || 1;
  const NH = capture?.naturalH || 1;

  return (
    <div data-testid="visual-editor-overlay" className="fixed inset-0 z-[90] bg-[#050507] flex flex-col" style={topOffset ? { top: topOffset } : undefined}>
      {inline.measureFrameSrc && (
        // Phone-size check for "Edit text & images": laid out at 390 × 860
        // like a phone, beneath everything, never seen, clicked or focused.
        <iframe
          ref={measureFrameRef}
          src={inline.measureFrameSrc}
          title="Phone-size check (hidden)"
          aria-hidden="true"
          tabIndex={-1}
          inert=""
          data-testid="phone-check-frame"
          style={{ position: 'absolute', top: 0, left: 0, width: PHONE_WIDTH, height: PHONE_CHECK_HEIGHT, border: 0, opacity: 0, pointerEvents: 'none', zIndex: -1 }}
        />
      )}
      {/* Top bar */}
      <header className="flex items-center gap-3 px-4 md:px-6 py-3 border-b border-white/[0.06] bg-[#0a0a0f]/90 backdrop-blur-md">
        <button type="button" onClick={() => navigate('/portal/edits')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] text-[13px] text-gray-300 hover:text-white hover:border-white/[0.15] transition-all">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30">
            <SquareDashedMousePointer className="w-3 h-3 text-[#B0FC38]" />
            <span className="text-[10px] font-medium text-white tracking-wider uppercase">Visual editor</span>
          </div>
          <span className="text-[12px] text-gray-500 truncate">{account.name} · {view.path}</span>
        </div>
        {inlineAvailable && !capture && initialSrc && (
          <button type="button" role="switch" aria-checked={editMode} onClick={toggleEditMode} disabled={inlineBusy}
            title={inlineBusy ? 'Wait until publishing finishes' : undefined}
            className={`inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-full border text-[13px] transition-all disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/60 ${editMode ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.12] text-white' : 'border-white/[0.08] bg-white/[0.02] text-gray-300 hover:text-white hover:border-white/[0.15]'}`}>
            <PencilLine className="w-3.5 h-3.5" aria-hidden="true" /> Edit text &amp; images
            <span aria-hidden="true" className={`relative w-7 h-4 rounded-full transition-colors ${editMode ? 'bg-[#B0FC38]' : 'bg-white/[0.15]'}`}>
              <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-[#0a0a0f] transition-all ${editMode ? 'left-3.5' : 'left-0.5'}`} />
            </span>
          </button>
        )}
        {!capture && (
          <>
            <button type="button" onClick={goHome} title="Home page" className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]"><Home className="w-4 h-4" /></button>
            <button type="button" onClick={reloadFrame} title="Reload" className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]"><RefreshCw className="w-4 h-4" /></button>
            {account.siteUrl && (
              <a href={`${siteOrigin}${view.path === '/' ? '' : view.path}`} target="_blank" rel="noopener noreferrer" title="Open live site" className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]"><ExternalLink className="w-4 h-4" /></a>
            )}
          </>
        )}
      </header>

      {/* Body */}
      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        {/* Left: live site OR the captured screenshot to annotate */}
        <div className={`flex-1 relative overflow-hidden ${phoneFrame ? 'bg-[#0c0c12]' : 'bg-white'}`}>
          {!capture ? (
            <>
              {initialSrc ? (
                // Phone view (edit mode): a 390px frame centred on a dark backdrop.
                <div data-testid="site-frame" className={phoneFrame ? 'h-full mx-auto shadow-2xl shadow-black/60 bg-white' : 'w-full h-full'} style={phoneFrame ? { width: PHONE_WIDTH, maxWidth: '100%' } : undefined}>
                  <iframe ref={iframeRef} src={initialSrc} title="Site preview" onLoad={inline.onFrameLoad} className="w-full h-full border-0" />
                </div>
              ) : (
                <div className="absolute inset-0 grid place-items-center bg-[#050507] text-gray-500 text-sm">No site URL configured for this account.</div>
              )}
              {/* Floating capture CTA (hidden while editing text & images) */}
              {initialSrc && !editMode && (
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2">
                  <button type="button" data-tour="capture" onClick={captureView} disabled={capturing}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold shadow-2xl shadow-black/40 hover:bg-[#9BE22E] disabled:opacity-60">
                    {capturing ? (<><span className="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" /> Capturing…</>) : (<><Camera className="w-4 h-4" /> Capture this view to request a change</>)}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 overflow-auto bg-[#0c0c12] p-6 flex items-start justify-center">
              <div className="relative inline-block shadow-2xl" data-tour="screenshot">
                <img
                  ref={imgRef}
                  src={capture.dataUrl}
                  onLoad={onImgLoad}
                  alt="Captured page"
                  draggable={false}
                  onMouseDown={onDrawStart}
                  onMouseMove={onDrawMove}
                  onMouseUp={onDrawEnd}
                  className="block max-w-full select-none cursor-crosshair"
                  style={{ maxHeight: 'calc(100vh - 160px)' }}
                />
                {box && (
                  <div className="absolute border-2 pointer-events-none"
                    style={{
                      borderColor: BOX_STROKE,
                      background: 'rgba(155,80,224,0.14)',
                      left: `${(box.x / NW) * 100}%`,
                      top: `${(box.y / NH) * 100}%`,
                      width: `${(box.w / NW) * 100}%`,
                      height: `${(box.h / NH) * 100}%`,
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: instructions / form */}
        <aside data-tour="markup-form" className="w-full md:w-[380px] h-[46%] md:h-auto shrink-0 border-t md:border-t-0 md:border-l border-white/[0.06] bg-[#0a0a0f] flex flex-col overflow-hidden">
          {editMode && !capture ? (
            <InlineEditPanel
              bridge={inline}
              siteOrigin={siteOrigin}
              onOpenPage={openFramePath}
              onBusyChange={setInlineBusy}
              initialStatus={inlineResume}
            />
          ) : (
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div key="submitted" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col p-5 gap-4">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-[#B0FC38]/[0.08] border border-[#B0FC38]/25">
                  <CheckCircle2 className="w-4 h-4 text-[#B0FC38] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-white">Edit request submitted</div>
                    <div className="text-[12px] text-gray-400 mt-1 leading-relaxed">NexSav will review it. The screenshot you marked goes straight to the team. Track status in your Edit Requests.</div>
                  </div>
                </div>
                <button type="button" onClick={() => setSubmitted(null)} className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-[#B0FC38]/[0.15] border border-[#B0FC38]/30 text-sm text-white hover:bg-[#B0FC38]/[0.25]">Request another change</button>
                <Link to="/portal/edits" className="text-center text-[12px] text-gray-500 hover:text-white">View all requests →</Link>
              </motion.div>
            ) : capture ? (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col overflow-hidden">
                <div className="p-5 border-b border-white/[0.05] flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30 grid place-items-center shrink-0">
                    <SquareDashedMousePointer className="w-3.5 h-3.5 text-[#B0FC38]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-medium text-gray-500 tracking-wider uppercase">Marking up</div>
                    <div className="text-sm text-white font-medium truncate mt-0.5">{view.title || account.name}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5 font-mono truncate">{capture.pagePath}</div>
                  </div>
                  <button type="button" onClick={resetCapture} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]" title="Back to browsing"><X className="w-3.5 h-3.5" /></button>
                </div>

                {!hasBox && (
                  <div className="mx-5 mt-4 flex items-start gap-2 text-[11px] text-[#B0FC38] bg-[#B0FC38]/[0.08] border border-[#B0FC38]/20 rounded-lg px-3 py-2">
                    <SquareDashedMousePointer className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>Drag a box on the screenshot around the area you want changed.</span>
                  </div>
                )}

                <form onSubmit={onSubmit} className="flex-1 flex flex-col overflow-hidden" noValidate>
                  <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
                    <div>
                      <label className={labelClasses} htmlFor="visual-title">Title</label>
                      <input id="visual-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
                        className={inputClasses} placeholder="Short summary of the change" />
                    </div>
                    <div>
                      <label className={labelClasses}>Priority</label>
                      <div className="grid grid-cols-2 gap-1.5">
                        {PRIORITIES.map((p) => {
                          const sel = priority === p.value; const PIcon = p.icon;
                          return (
                            <button key={p.value} type="button" onClick={() => setPriority(p.value)}
                              className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-[11px] transition-all ${sel ? p.ring + ' text-white' : 'bg-white/[0.02] border-white/[0.06] text-gray-400 hover:border-white/[0.12]'}`}>
                              <PIcon className={`w-3.5 h-3.5 ${p.color}`} /> {p.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className={labelClasses}>Change type</label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {CHANGE_TYPES.map((c) => {
                          const sel = changeType === c.value; const CIcon = c.icon;
                          return (
                            <button key={c.value} type="button" onClick={() => setChangeType(c.value)}
                              className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-[11px] transition-all ${sel ? 'bg-[#B0FC38]/[0.12] border-[#B0FC38]/40 text-white' : 'bg-white/[0.02] border-white/[0.06] text-gray-400 hover:border-white/[0.12] hover:text-gray-200'}`}>
                              <CIcon className="w-3.5 h-3.5" /> {c.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {showBeforeAfter && (
                      <div className="flex flex-col gap-2 p-3 rounded-lg bg-white/[0.015] border border-white/[0.05]">
                        <div>
                          <label className={labelClasses} htmlFor="visual-current">{changeType === 'link' ? 'Current link' : 'Current text'}</label>
                          <textarea id="visual-current" value={currentContent} onChange={(e) => setCurrentContent(e.target.value)} rows={2}
                            className={`${inputClasses} resize-y`} placeholder={changeType === 'link' ? 'The link as it is now' : 'The text as it appears now'} />
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-gray-600 uppercase tracking-wider"><ArrowRight className="w-3 h-3" /> changes to</div>
                        <div>
                          <label className={labelClasses} htmlFor="visual-desired">{changeType === 'link' ? 'New link' : 'What it should say'}</label>
                          <textarea id="visual-desired" value={desiredContent} onChange={(e) => setDesiredContent(e.target.value)} rows={2}
                            className={`${inputClasses} resize-y`} placeholder={changeType === 'link' ? 'Where it should point instead' : 'The exact new wording'} />
                        </div>
                      </div>
                    )}
                    <div>
                      <label className={labelClasses} htmlFor="visual-desc">Description</label>
                      <textarea id="visual-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={5} className={`${inputClasses} resize-y min-h-[96px]`} placeholder="What should change in the marked area?" required />
                    </div>
                    <div>
                      <label className={labelClasses}>Reference files</label>
                      <FileUploadField attachments={attachments} onChange={setAttachments} disabled={submitting} scope="edit-requests" />
                    </div>
                    {error && <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[12px] text-red-300/90">{error}</div>}
                  </div>
                  <div className="p-4 border-t border-white/[0.05] bg-white/[0.01] flex items-center gap-2">
                    <button type="button" onClick={resetCapture} className="px-4 py-2.5 rounded-full border border-white/[0.08] text-[13px] text-gray-300 hover:text-white">Recapture</button>
                    <button type="submit" data-tour="submit-request" disabled={submitting || !title.trim() || !description.trim()} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 disabled:opacity-50">
                      {submitting ? (<><span className="w-3.5 h-3.5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" /> Submitting…</>) : (<><Send className="w-3.5 h-3.5" /> Submit request</>)}
                    </button>
                  </div>
                </form>
              </motion.div>
            ) : (
              <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col p-5 gap-5">
                <div>
                  <h2 className="text-lg font-semibold text-white tracking-tight">Browse, then capture</h2>
                  <p className="text-[13px] text-gray-400 leading-relaxed mt-2">This is your real, live site. Click around exactly like a visitor. When you reach what you want changed, hit <span className="text-white font-medium">Capture this view</span>, draw a box, and describe the change.</p>
                </div>
                <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-4 space-y-3">
                  <Step num={1} title="Navigate">Use the site's own menus and links to get to the right page.</Step>
                  <Step num={2} title="Capture">Click "Capture this view" and we take a clean snapshot of exactly what you see.</Step>
                  <Step num={3} title="Mark">Drag a box around the area you want changed.</Step>
                  <Step num={4} title="Describe">Pick a change type, describe it, attach files, submit.</Step>
                </div>
                {!bridgeSeen && (
                  <div className="flex items-start gap-2 text-[11px] text-gray-500 leading-relaxed mt-auto">
                    <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>Waiting for the site to connect… if it doesn't, you can still capture the current page.</span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          )}
        </aside>
      </div>

      {showTour && (
        <SpotlightTour
          onClose={closeTour}
          onFinish={closeTour}
          steps={[
            {
              target: null,
              eyebrow: 'Visual editor',
              title: 'This is your live site',
              body: 'Everything here is your real website. Click its links and menus to get to the page you want, exactly like a visitor would.',
            },
            {
              target: '[data-tour="capture"]',
              placement: 'top',
              title: '1 · Capture the page',
              body: 'When you reach what you want changed, you snapshot it. Hit Next and watch: we’ll capture this page to show you.',
            },
            {
              target: '[data-tour="screenshot"]',
              placement: 'right',
              title: '2 · Mark the spot',
              body: 'We take a clean snapshot, then you drag a box around exactly what you want changed, like the one we just drew.',
              onEnter: runTourCapture,
            },
            {
              target: '[data-tour="markup-form"]',
              placement: 'left',
              title: '3 · Describe it',
              body: 'Pick a change type and priority, give the exact before → after for text, and add a short description. (We’ve filled in an example.)',
            },
            {
              target: '[data-tour="submit-request"]',
              placement: 'left',
              title: '4 · Submit',
              body: 'Then hit Submit and the team gets your request with the marked-up screenshot, ready to make. That’s the whole flow. No need to actually submit right now.',
              finishLabel: 'Got it, I’ll try it',
            },
          ]}
        />
      )}
    </div>
  );
}

// Draws the screenshot + (optional) accent box into a PNG data URL. Same-origin
// data URL source means the canvas isn't tainted, so export works.
function compositeBox(dataUrl, box) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        if (box && box.w > 4 && box.h > 4) {
          ctx.fillStyle = 'rgba(155,80,224,0.16)';
          ctx.fillRect(box.x, box.y, box.w, box.h);
          ctx.strokeStyle = BOX_STROKE;
          ctx.lineWidth = Math.max(3, img.naturalWidth / 350);
          ctx.strokeRect(box.x, box.y, box.w, box.h);
        }
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function Step({ num, title, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-5 h-5 rounded-full bg-[#B0FC38]/[0.15] border border-[#B0FC38]/30 grid place-items-center text-[10px] font-semibold text-[#B0FC38] shrink-0">{num}</div>
      <div className="flex-1">
        <div className="text-[12px] font-medium text-white">{title}</div>
        <div className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
